import React, { useState } from 'react';
import { supabase } from '../services/supabaseService';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Wifi as WifiIcon,
  Pets as PetsIcon,
  LocalParking as ParkingIcon,
  Elevator as ElevatorIcon,
  Balcony as BalconyIcon,
  Kitchen as KitchenIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Home as HomeIcon,
  SquareFoot as SquareFootIcon,
  Layers as LayersIcon,
  AttachMoney as MoneyIcon,
  Payments as PaymentsIcon,
  LocationOn as LocationIcon
} from '@mui/icons-material';

const PropertyCarousel = ({ title, properties, onPropertyClick, userId, onFavoritesChange, favoriteIds: externalFavoriteIds, onLoginRequired }) => {
  // Ensure properties is always an array
  const safeProperties = Array.isArray(properties) ? properties : [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const [favorites, setFavorites] = useState(new Set());
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Synchronizuj lokalne favorites z App.js (np. po usunięciu z modala)
  React.useEffect(() => {
    if (externalFavoriteIds !== undefined && initialLoaded) {
      setFavorites(new Set(externalFavoriteIds.map(id => Number(id))));
    }
  }, [externalFavoriteIds, initialLoaded]);

  // Load favorites from Supabase when component mounts or userId changes
  React.useEffect(() => {
    if (!userId) {
      setFavorites(new Set());
      setInitialLoaded(true);
      return;
    }
    (async () => {
      const { data, error, status } = await supabase
        .from('users')
        .select('favorites')
        .eq('id', userId)
        .single();
      if (error) {
        setInitialLoaded(true);
        return;
      }
      if (data && Array.isArray(data.favorites) && data.favorites.length > 0) {
        const favoriteIds = data.favorites.map(id => Number(id));
        const { data: existingProperties, error: propError } = await supabase
          .from('properties')
          .select('id')
          .in('id', favoriteIds);
        
        if (propError) {
          setFavorites(new Set());
          setInitialLoaded(true);
          return;
        }
        
        const validIds = new Set((existingProperties || []).map(p => Number(p.id)));
        setFavorites(validIds);
        
        if (validIds.size !== favoriteIds.length) {
          await supabase
            .from('users')
            .update({ favorites: Array.from(validIds) })
            .eq('id', userId);
        }
      } else {
        setFavorites(new Set());
      }
      setInitialLoaded(true);
    })();
  }, [userId]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const itemsPerView = isMobile ? 1 : 3;
  // Card width for desktop carousel
  const cardWidth = 320 + 16; // 320px card + 16px gap
  // Maximum index for carousel navigation
  const maxIndex = Math.max(0, safeProperties.length - itemsPerView);

  // Carousel navigation handlers
  const prevSlide = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };
  const nextSlide = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  // Touch/swipe handlers for mobile
  let touchStartX = null;
  const handleTouchStart = (e) => {
    touchStartX = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX === null) return;
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchEndX - touchStartX;
    if (diff > 50 && currentIndex > 0) {
      prevSlide();
    } else if (diff < -50 && currentIndex < maxIndex) {
      nextSlide();
    }
    touchStartX = null;
  };

  // Favorite toggle handler
  const toggleFavorite = async (propertyId) => {
    if (!userId) {
      if (onLoginRequired) onLoginRequired();
      return;
    }
    setFavorites((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId);
      } else {
        newSet.add(propertyId);
      }
      // Log aktualizowane ulubione i userId
      // Zapisz ulubione w Supabase jako tablicę ID (integer[])
      if (userId) {
        const favoritesArray = Array.from(newSet).map(id => Number(id));
        supabase
          .from('users')
          .update({ favorites: favoritesArray })
          .eq('id', userId)
          .select('id, favorites')
          .then((result) => {
            if (result.error) {}
            else if (!result.data || result.data.length === 0) {
            } else {
              // Powiadom App.js o zmianie ulubionych
              if (onFavoritesChange) onFavoritesChange(favoritesArray.length);
            }
          });
      }
      return newSet;
    });
  };

  // Rooms label helper
  const getRoomsLabel = (rooms) => {
    if (rooms === 0) return 'Pokój';
    if (rooms === 1) return 'Kawalerka';
    if (rooms > 1) return `${rooms} ${rooms <= 4 ? 'pokoje' : 'pokoi'}`;
    return '— pokoi';
  };
    return (
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#2D3748' }}>{title}</Typography>
        <Box sx={{ position: 'relative', overflow: 'hidden', width: '100%' }}>
          {/* Navigation Arrows */}
          {!isMobile && (
            <>
              <IconButton
                onClick={prevSlide}
                disabled={currentIndex === 0}
                sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, backgroundColor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', '&:hover': { backgroundColor: '#f8f9fa' }, '&.Mui-disabled': { opacity: 0.3 } }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                onClick={nextSlide}
                disabled={currentIndex === maxIndex}
                sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2, backgroundColor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', '&:hover': { backgroundColor: '#f8f9fa' }, '&.Mui-disabled': { opacity: 0.3 } }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}
          {/* Cards Container */}
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 0, sm: 2 },
              width: 'auto',
              transform: `translateX(-${currentIndex * (isMobile ? 100 : cardWidth)}${isMobile ? '%' : 'px'})`,
              transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {safeProperties.map((property, index) => (
              <Card
                key={index}
                onClick={() => onPropertyClick && onPropertyClick(property)}
                sx={{
                  width: isMobile ? '100%' : '320px',
                  minWidth: isMobile ? '100%' : '320px',
                  maxWidth: isMobile ? '100%' : '320px',
                  flexShrink: 0,
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer'
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  {/* Label Wyróżnione dla premium */}
                  {property.is_premium && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        left: '2px',
                        backgroundColor: 'rgba(248, 223, 99, 0.88)',
                        backdropFilter: 'blur(6px)',
                        color: '#111',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        px: 1.2,
                        py: 0.3,
                        borderRadius: '6px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        zIndex: 5,
                        letterSpacing: '0.5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      Promowane
                    </Box>
                  )}
                  <CardMedia
                    component="img"
                    height="160"
                    image={Array.isArray(property.image_url) ? property.image_url[0] : property.image_url}
                    alt={property.title}
                    onLoad={() => {
                    }}
                    onError={(e) => {
                    }}
                    sx={{ objectFit: 'cover' }}
                  />
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(property.id);
                    }}
                    sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(10px)', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' } }}
                  >
                    {favorites.has(property.id) ? (
                      <FavoriteIcon sx={{ color: '#D2691E' }} />
                    ) : (
                      <FavoriteBorderIcon sx={{ color: '#666' }} />
                    )}
                  </IconButton>
                </Box>
                <CardContent sx={{ pt: 1.5, px: 1.5, pb: 1.2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <LocationIcon sx={{ color: '#D32F2F', fontSize: 16, mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem' }}>
                      {[property.city, property.street?.trim(), property.district?.trim()].filter(Boolean).join(', ') || property.address}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', fontSize: '0.95rem', lineHeight: 1.1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{property.title}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1, alignItems: 'center' }}>
                    {property.previous_price && (
                      <Chip 
                        label={(() => {
                          const prevRent = Number(property.rent) || 0;
                          const prevPrice = Number(property.previous_price) || 0;
                          const prevTotal = prevRent + prevPrice;
                          return prevTotal > 0 ? `${prevTotal.toLocaleString('pl-PL')} zł` : '';
                        })()} 
                        size="small" 
                        sx={{ backgroundColor: '#FFEBEE', color: '#C62828', fontWeight: 'bold', fontSize: '0.7rem', textDecoration: 'line-through' }} 
                      />
                    )}
                    <Chip 
                      label={(() => {
                        const rent = Number(property.rent) || 0;
                        const price = Number(property.price) || 0;
                        const totalPrice = rent + price;
                        return totalPrice > 0 ? `${totalPrice.toLocaleString('pl-PL')} zł/mies.` : '— zł/mies.';
                      })()} 
                      size="small" 
                      sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 'bold', fontSize: '0.75rem' }} 
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip icon={<HomeIcon fontSize="small" />} label={getRoomsLabel(property.rooms)} size="small" sx={{ backgroundColor: '#F7FAFC', color: '#4A5568', fontSize: '0.75rem', '& .MuiChip-icon': { color: '#666' } }} />
                    <Chip icon={<SquareFootIcon fontSize="small" />} label={property.area ? `${property.area} m²` : '— m²'} size="small" sx={{ backgroundColor: '#F7FAFC', color: '#4A5568', fontSize: '0.75rem', '& .MuiChip-icon': { color: '#666' } }} />
                    <Chip icon={<LayersIcon fontSize="small" />} label={property.floor !== undefined && property.total_floors !== undefined ? `piętro ${property.floor}/${property.total_floors}` : property.floor !== undefined ? `piętro ${property.floor}` : 'piętro —'} size="small" sx={{ backgroundColor: '#F7FAFC', color: '#4A5568', fontSize: '0.75rem', '& .MuiChip-icon': { color: '#666' } }} />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
          {isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 1 }}>
              {safeProperties.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: index === currentIndex ? '#D2691E' : '#CBD5E0', cursor: 'pointer', transition: 'all 0.2s ease' }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  export default PropertyCarousel;